import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { getLeaguesApiAxiosInstance } from '../../utils/network';

import ErrorPage from '../../components/ErrorPage';

import { getClubLeagues } from '../../api/clubsApi';
import { setClubId } from '../../actions/club-actions';
import { setHeader } from '../../actions/header-actions';
import { useStatsig } from '../../components/hooks/useStatsig';
import { getStandardErrorPageConfig } from '../../utils/errorPage';
import config from '../../../config';
import ClubProfilePage from './ClubProfilePage';
import PlayersGroupProfilePage from './PlayersGroupProfilePage';
import { getNewClubPath } from '../../utils/clubs';
import { showSplash } from '../../actions/splash-actions';

const ClubProfileWrapper = (props) => {
    const dispatch = useDispatch();
    const club = useSelector((state) => state.club);
    const user = useSelector((state) => state.auth?.user);
    const { initializeStatsigUser, featureGates, checkGate } = useStatsig();

    const [showLeaguesTab, setShowLeaguesTab] = useState(false);
    const [clubLeagues, setClubLeagues] = useState([]);

    const getClub = () => {
        dispatch(setClubId(props.match.params.clubId, true));

        if (config.oldLogoClubIds?.length &&
            config.oldLogoClubIds.includes(parseInt(props.match.params.clubId))) {
            dispatch(setHeader({
                oldLogo: true,
            }));
        }
    };

    useEffect(() => {
        if (!club?.clubId) {
            getClub();
        }
    }, []);

    useEffect(() => {
        if (user) {
            initializeStatsigUser();
        }
    }, [user]);

    useEffect(() => {
        const getClubLeaguesTab = async () => {
            const { data } = await getClubLeagues(getLeaguesApiAxiosInstance(), club.clubId);
            const hasClubLeagues = data?.leagues.length > 0;

            if (hasClubLeagues) {
                setClubLeagues(data?.leagues);
            }

            setShowLeaguesTab(hasClubLeagues);
        };

        if (club?.clubId) {
            getClubLeaguesTab();
        }

        if (club?.clubId && club.clubId !== props.match.params.clubId) {
            getClub();
        }

        return () => {
            dispatch(setHeader({
                oldLogo: false,
            }));
        };
    }, [club?.clubId]);

    const renderClubWrapper = clubTierTypeId => {
        // handle Players Group and Free Play Club Types
        const clubWrapperDictionary = {
            1: <PlayersGroupProfilePage {...props} />, // Pro
            11: <Redirect to={'/'} />, // Create Free Play
        };
        const clubTypeExists = clubTierTypeId in clubWrapperDictionary;
        if (clubTypeExists) {
            return clubWrapperDictionary[clubTierTypeId];
        }

        // handle next.js redirections if feature gate is enabled
        const isClubRedirectEnabled = checkGate(featureGates.CLUB_LINKS_REDIRECT);
        const newClubPath = getNewClubPath(clubTierTypeId);
        if (isClubRedirectEnabled && clubTierTypeId) {
            dispatch(showSplash(true));
            window.location.href = `${config.nextAppHost}/${newClubPath}/${club?.clubId}`;
        }

        // render legacy Club Profile Page
        return <ClubProfilePage {...props} showLeaguesTab={showLeaguesTab} clubLeagues={clubLeagues} />;
    };

    if (club?.clubDetailsFetched && !club?.clubDetails) {
        const errorConfig = getStandardErrorPageConfig({ errorData: club?.error, resourceName: 'club' });

        return (
            <ErrorPage
                subtitle={errorConfig.subtitle}
                showReloadButton={errorConfig.showReloadButton}
                showBackButton={errorConfig.showBackButton}
                data-testid="club-profile.error-page"
            />
        );
    }

    const clubTierTypeId = club?.clubDetails?.tierTypeId || club?.clubDetails?.tierType?.id;

    return (club?.clubDetailsFetched && club.clubDetails ? (
        <>
            {renderClubWrapper(clubTierTypeId)}
        </>
    ) : null);
};

ClubProfileWrapper.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            clubId: PropTypes.string.isRequired,
        }),
    }).isRequired,
};

export default compose(ClubProfileWrapper);
